import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Toast } from "primereact/toast";
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Collapse,
  Drawer,
  Form,
  InputNumber,
  Modal,
  Select,
  Spin,
  Steps,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  BarcodeOutlined,
  BoxPlotOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  LockOutlined,
  SaveOutlined,
  SearchOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import debounce from "lodash.debounce";
import Cookies from "js-cookie";
import config from "../config.js";
import "./DeliveryPage.css";

const { Title, Text } = Typography;
const { Option } = Select;

const DeliveryPage = ({ darkTheme = false }) => {
  // ─── Core State ───
  const [searchAwbCode, setSearchAwbCode] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Ready to scan or enter AWB Code");
  const [deliveryStage, setDeliveryStage] = useState("INITIAL");
  const [currentTime, setCurrentTime] = useState(moment().format("HH:mm:ss"));

  // ─── Scanner ───
  const [scanBuffer, setScanBuffer] = useState("");
  const lastKeyTimeRef = useRef(0);

  // ─── Form ───
  const [form] = Form.useForm();
  const [showWeightInput, setShowWeightInput] = useState(false);
  const [totalProductsWeight, setTotalProductsWeight] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isBoxTypeEditing, setIsBoxTypeEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [boxTypes, setBoxTypes] = useState([{ type: "", quantity: 1 }]);

  // ─── Default Box ───
  const [defaultBoxEnabled, setDefaultBoxEnabled] = useState(false);
  const [defaultBoxType, setDefaultBoxType] = useState("");
  const [hasDefaultBox, setHasDefaultBox] = useState(false);
  const [showDefaultSettings, setShowDefaultSettings] = useState(false);
  const [packingMaterials, setPackingMaterials] = useState([]);

  // ─── Drawer ───
  const [productsDrawerOpen, setProductsDrawerOpen] = useState(false);

  const toast = useRef(null);
  const allowedStatuses = ["PICKUP EXCEPTION", "OUT FOR PICKUP", "PICKUP SCHEDULED"];

  // Extract weight from SKU (e.g., "100g OA Powder" → 0.1 kg, "1kg Test" → 1 kg)
  const extractWeightFromSku = (sku) => {
    if (!sku) return 0;
    const match = sku.match(/(\d+(?:\.\d+)?)\s*(g|kg|mg|ml|l)\b/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    const unit = match[2].toLowerCase();
    switch (unit) {
      case "kg": return value;
      case "g": return value / 1000;
      case "mg": return value / 1000000;
      case "l": return value;
      case "ml": return value / 1000;
      default: return 0;
    }
  };

  // Get effective weight: use weight field, fallback to SKU parsing
  const getEffectiveWeight = (product) => {
    const fieldWeight = product.weight || 0;
    if (fieldWeight > 0) return fieldWeight;
    return extractWeightFromSku(product.sku || product.name || "");
  };
  const parseBoxTypes = (boxType) => {
    if (!boxType) return [];
    return String(boxType).split(",").map((item) => {
      const match = item.trim().match(/^(.+?)(?:\s*\((\d+)\))?$/);
      return match
        ? { type: match[1].trim(), quantity: parseInt(match[2], 10) || 1 }
        : { type: item.trim(), quantity: 1 };
    }).filter((box) => box.type);
  };

  // ─── Load default ───
  useEffect(() => {
    const saved = localStorage.getItem("delivery_default_box");
    if (saved) {
      try {
        const p = JSON.parse(saved);
        setDefaultBoxType(p.type || "");
        setDefaultBoxEnabled(p.enabled !== false);
        setHasDefaultBox(true);
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    let active = true;
    const token = Cookies.get("token") || localStorage.getItem("token");

    axios.get(`${config.apiBaseUrl}/api/packing-materials`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((response) => {
      if (!active) return;
      const materials = (response.data?.materials || [])
        .filter((material) => material.materialType === "Box" && material.status === "Active")
        .sort((a, b) => String(a.materialName || "").localeCompare(String(b.materialName || "")));
      setPackingMaterials(materials);
    }).catch((error) => {
      if (active) setPackingMaterials([]);
      console.error("Failed to load packing materials for delivery:", error);
    });

    return () => { active = false; };
  }, []);

  // ─── Clock ───
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(moment().format("HH:mm:ss")), 1000);
    return () => clearInterval(t);
  }, []);

  // ─── Scanner ───
  useEffect(() => {
    const handler = (e) => {
      // Never capture keys when user is typing in an input
      const tag = e.target?.tagName;
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || e.target?.closest?.(".ant-input-number") || e.target?.closest?.(".ant-select");
      if (isInput) return;
      if (e.ctrlKey || e.altKey || e.metaKey || e.key.length > 1) return;
      // Allow scanning at any stage (INITIAL, SCANNING, DELIVERING, COMPLETED)
      setScanBuffer((p) => p + e.key);
      lastKeyTimeRef.current = Date.now();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const debounced = debounce(() => {
      if (scanBuffer.length > 0) {
        handleSearch(scanBuffer.trim());
        setScanBuffer("");
      }
    }, 200);
    debounced();
    return () => debounced.cancel();
  }, [scanBuffer]);

  // ─── Default Box Helpers ───
  const saveDefaultBox = useCallback(() => {
    if (!defaultBoxType) {
      toast.current?.show({ severity: "warn", summary: "No type", detail: "Select a box type first." });
      return;
    }
    localStorage.setItem("delivery_default_box", JSON.stringify({ type: defaultBoxType, enabled: true }));
    setDefaultBoxEnabled(true);
    setHasDefaultBox(true);
    toast.current?.show({ severity: "success", summary: "Saved", detail: `"${defaultBoxType}" set as default.` });
  }, [defaultBoxType]);

  const toggleDefaultBox = useCallback((checked) => {
    setDefaultBoxEnabled(checked);
    if (hasDefaultBox) {
      const s = JSON.parse(localStorage.getItem("delivery_default_box") || "{}");
      s.enabled = checked;
      localStorage.setItem("delivery_default_box", JSON.stringify(s));
    }
  }, [hasDefaultBox]);

  const clearDefaultBox = useCallback(() => {
    localStorage.removeItem("delivery_default_box");
    setDefaultBoxType("");
    setDefaultBoxEnabled(false);
    setHasDefaultBox(false);
    toast.current?.show({ severity: "info", summary: "Cleared", detail: "Default box removed." });
  }, []);

  const applyDefaultBox = useCallback(() => {
    if (defaultBoxEnabled && hasDefaultBox && defaultBoxType) {
      setBoxTypes([{ type: defaultBoxType, quantity: 1 }]);
    } else {
      setBoxTypes([{ type: "", quantity: 1 }]);
    }
  }, [defaultBoxEnabled, hasDefaultBox, defaultBoxType]);

  // ─── Reset ───
  const resetState = () => {
    setOrderDetails(null);
    setSearchAwbCode("");
    setStatusMessage("Ready to scan or enter AWB Code");
    setDeliveryStage("INITIAL");
    setShowWeightInput(false);
    setTotalProductsWeight(0);
    setIsEditing(false);
    setIsBoxTypeEditing(false);
    setBoxTypes(defaultBoxEnabled && hasDefaultBox ? [{ type: defaultBoxType, quantity: 1 }] : [{ type: "", quantity: 1 }]);
    form.resetFields();
  };

  // ─── Search ───
  const handleSearch = async (awb) => {
    if (!awb) {
      toast.current?.show({ severity: "warn", summary: "Missing AWB", detail: "Enter an AWB code." });
      return;
    }
    if (orderDetails && deliveryStage === "DELIVERING") {
      if (awb === orderDetails._scannedAwb) {
        toast.current?.show({ severity: "info", summary: "Loaded", detail: "Order already loaded." });
        return;
      }
      resetState();
    }

    setLoading(true);
    setOrderDetails(null);
    setStatusMessage(`Fetching AWB: ${awb}...`);
    setDeliveryStage("SCANNING");

    try {
      const res = await axios.get(`${config.apiBaseUrl}/order/awb/${encodeURIComponent(awb)}`);
      if (!res.data.order) {
        setStatusMessage("No order found.");
        setDeliveryStage("INITIAL");
        toast.current?.show({ severity: "error", summary: "Not found", detail: "No order found." });
        setLoading(false);
        return;
      }

      const order = res.data.order;
      const shipment = order.shipments?.find((s) => s.awb_code?.includes(awb));
      const status = shipment ? shipment.status : "N/A";

      const totalWeight = (order.products || []).reduce((s, p) => s + getEffectiveWeight(p) * (p.quantity || 0), 0);
      setTotalProductsWeight(totalWeight);

      if (order.packed_status !== "Completed") {
        setStatusMessage("Order not packed yet.");
        setDeliveryStage("INITIAL");
        toast.current?.show({ severity: "warn", summary: "Not packed", detail: "Order must be packed." });
        setLoading(false);
        return;
      }
      if (!allowedStatuses.includes(status)) {
        setStatusMessage(`Cannot deliver with status: ${status}`);
        setDeliveryStage("INITIAL");
        toast.current?.show({ severity: "warn", summary: "Invalid", detail: `Status: ${status}` });
        setLoading(false);
        return;
      }
      if (order.warehouse_out === "Yes") {
        setStatusMessage("Already delivered.");
        setDeliveryStage("COMPLETED");
        order._scannedAwb = awb;
        setOrderDetails(order);
        setSearchAwbCode(awb);
        toast.current?.show({ severity: "info", summary: "Done", detail: "Already warehouse out." });
        setLoading(false);
        return;
      }

      order._scannedAwb = awb;
      setOrderDetails(order);
      setSearchAwbCode(awb);
      setStatusMessage(`AWB ${awb} loaded. Enter delivery details.`);
      setDeliveryStage("DELIVERING");
      setShowWeightInput(true);
      setIsBoxTypeEditing(false);
      const packedBoxTypes = parseBoxTypes(order.box_type);
      setBoxTypes(packedBoxTypes.length > 0
        ? packedBoxTypes
        : (defaultBoxEnabled && hasDefaultBox ? [{ type: defaultBoxType, quantity: 1 }] : [{ type: "", quantity: 1 }]));
      toast.current?.show({ severity: "success", summary: "Loaded", detail: "Fill in weight and box type." });
    } catch {
      setStatusMessage("Failed to fetch order.");
      setDeliveryStage("INITIAL");
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch order." });
    } finally {
      setLoading(false);
    }
  };

  // ─── Box Handlers ───
  const handleAddBoxType = () => setBoxTypes([...boxTypes, { type: "", quantity: 1 }]);
  const handleRemoveBoxType = (i) => {
    const n = [...boxTypes];
    n.splice(i, 1);
    setBoxTypes(n.length > 0 ? n : [{ type: "", quantity: 1 }]);
  };
  const handleBoxTypeChange = (i, field, val) => {
    const n = [...boxTypes];
    n[i] = { ...n[i], [field]: val };
    setBoxTypes(n);
  };

  // ─── Submit ───
  const handleWeightSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (boxTypes.some((b) => !b.type)) {
        toast.current?.show({ severity: "error", summary: "Missing", detail: "Select box type for all entries." });
        return;
      }
      if (values.weight < totalProductsWeight) {
        toast.current?.show({ severity: "error", summary: "Invalid", detail: `Weight must be ≥ ${totalProductsWeight} kg.` });
        return;
      }

      const boxStr = boxTypes.map((b) => `${b.type}${b.quantity > 1 ? ` (${b.quantity})` : ""}`).join(", ");
      const warehouseOutBy = Cookies.get("userName") || "Unknown";
      setSubmitting(true);
      const woDate = new Date().toISOString();
      const woTime = moment().format("HH:mm:ss");

      await axios.post(`${config.apiBaseUrl}/order/awb/${encodeURIComponent(orderDetails._scannedAwb)}/delivery`, {
        warehouse_out: "Yes",
        warehouse_out_date: woDate,
        warehouse_out_time: woTime,
        final_package_weight: values.weight,
        box_type: boxStr,
        warehouse_out_by: warehouseOutBy,
        is_edited: isEditing,
      });

      setOrderDetails((p) => ({ ...p, warehouse_out: "Yes", warehouse_out_date: woDate, warehouse_out_time: woTime, warehouse_out_by: warehouseOutBy, final_package_weight: values.weight, box_type: boxStr }));
      setStatusMessage("Delivery completed.");
      setDeliveryStage("COMPLETED");
      setShowWeightInput(false);
      setIsEditing(false);
      toast.current?.show({ severity: "success", summary: "Done", detail: "Delivery confirmed!" });
    } catch (e) {
      if (e.errorFields) return;
      toast.current?.show({ severity: "error", summary: "Failed", detail: "Failed to update." });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Edit ───
  const handleEdit = () => {
    if (!orderDetails) return;
    setIsEditing(true);
    setIsBoxTypeEditing(false);
    setShowWeightInput(true);
    setDeliveryStage("DELIVERING");
    setStatusMessage(`Editing AWB: ${orderDetails._scannedAwb}`);
    form.setFieldsValue({ weight: orderDetails.final_package_weight });
    if (orderDetails.box_type) {
      setBoxTypes(parseBoxTypes(orderDetails.box_type));
    }
  };

  const enableBoxTypeEditing = () => {
    Modal.confirm({
      title: "Change packed box type?",
      content: "The box type came from Packing. Change it only if the physical package is different.",
      okText: "Change Box Type",
      cancelText: "Keep Current",
      onOk: () => setIsBoxTypeEditing(true),
    });
  };

  const clearOrder = () => {
    resetState();
    toast.current?.show({ severity: "info", summary: "Cleared", detail: "Ready for next AWB." });
  };

  // ─── Computed ───
  const shipment = orderDetails?.shipments?.[0] || {};
  const customer = orderDetails?.customer || {};
  const totalProducts = orderDetails?.products?.length || 0;
  const boxTypeLocked = Boolean(orderDetails?.box_type) && !isBoxTypeEditing;
  const packingMaterialOptions = packingMaterials.map((material) => ({
    value: material.materialName,
    label: material.materialName,
  }));

  const stageTag = (() => {
    switch (deliveryStage) {
      case "INITIAL": return <Tag color="blue">Ready to Scan</Tag>;
      case "SCANNING": return <Tag color="orange">Loading</Tag>;
      case "DELIVERING": return <Tag color="purple">Fill Details</Tag>;
      case "COMPLETED": return <Tag color="green">Completed</Tag>;
      default: return <Tag color="blue">Ready</Tag>;
    }
  })();

  const productColumns = [
    { title: "Product", dataIndex: "name", key: "name" },
    { title: "Qty", dataIndex: "quantity", key: "quantity" },
    { title: "Weight (kg)", dataIndex: "weight", key: "weight", render: (w) => (w || 0).toFixed(2) },
    { title: "Total (kg)", key: "total", render: (_, r) => ((r.weight || 0) * (r.quantity || 0)).toFixed(2) },
  ];

  return (
    <main className={`delivery-page ${darkTheme ? "dark-theme" : ""}`}>
      <Toast ref={toast} />

      {/* ══════ COMMAND BAR ══════ */}
      {orderDetails && (
        <Card className="delivery-command-bar">
          <div className="delivery-command-title">
            <span className="delivery-command-icon"><InboxOutlined /></span>
            <span>
              <Text className="delivery-eyebrow">Warehouse Operations</Text>
              <Title level={4} style={{ margin: 0 }}>Delivery Module</Title>
            </span>
          </div>
          <div className="delivery-topbar-status">
            <Tag color="blue"><BarcodeOutlined /> {orderDetails._scannedAwb || searchAwbCode}</Tag>
            <Tag icon={<ClockCircleOutlined />}>{currentTime}</Tag>
          </div>
        </Card>
      )}

      {/* ══════ LOADING ══════ */}
      {loading && !orderDetails ? (
        <div className="delivery-loading"><Spin size="large" tip="Loading order..." /></div>
      ) : !orderDetails ? (
        /* ══════ LANDING ══════ */
        <div className="delivery-premium-landing">
          <div className="delivery-landing-content">
            <div className="delivery-landing-hero">
              <div className="delivery-landing-icon-wrap">
                <div className="delivery-landing-icon"><TruckOutlined /></div>
              </div>
              <Title level={2}>Delivery Module</Title>
              <Text className="delivery-landing-subtitle">
                Scan or enter an AWB code to begin warehouse out delivery.
              </Text>
            </div>
            <div className="delivery-landing-search-box">
              <div className="delivery-landing-search-inner">
                <span className="delivery-landing-search-prefix"><BarcodeOutlined /></span>
                <input
                  className="delivery-landing-search-input"
                  placeholder="Scan or enter AWB Code..."
                  value={searchAwbCode}
                  onChange={(e) => setSearchAwbCode(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch(searchAwbCode.trim())}
                  autoFocus
                />
                <button className="delivery-landing-search-btn" onClick={() => handleSearch(searchAwbCode.trim())} disabled={loading}>
                  {loading ? <Spin size="small" /> : <><SearchOutlined /> Search</>}
                </button>
              </div>
            </div>
            <div className="delivery-landing-features">
              <div className="delivery-feature-card">
                <div className="delivery-feature-icon"><SearchOutlined /></div>
                <Text strong>AWB Lookup</Text>
                <Text type="secondary">Find orders by AWB code</Text>
              </div>
              <div className="delivery-feature-card">
                <div className="delivery-feature-icon"><BoxPlotOutlined /></div>
                <Text strong>Box Selection</Text>
                <Text type="secondary">Select packaging type</Text>
              </div>
              <div className="delivery-feature-card">
                <div className="delivery-feature-icon"><TruckOutlined /></div>
                <Text strong>Warehouse Out</Text>
                <Text type="secondary">Process for dispatch</Text>
              </div>
              <div className="delivery-feature-card">
                <div className="delivery-feature-icon"><CheckCircleOutlined /></div>
                <Text strong>Confirm</Text>
                <Text type="secondary">Complete delivery</Text>
              </div>
            </div>
            <div className="delivery-landing-hint">
              <Text type="secondary">Press <Tag color="blue">Enter</Tag> or click Search</Text>
            </div>
          </div>
        </div>
      ) : (
        /* ══════ MAIN DELIVERY CARD ══════ */
        <div className="delivery-main-card">

          {/* ─── Info Strip: Customer + Shipment ─── */}
          <div className="delivery-info-strip">
            {/* Left: Customer */}
            <div className="delivery-info-left">
              <div className="delivery-info-header">
                <Avatar size={42} icon={<UserOutlined />} className="delivery-customer-avatar" />
                <div>
                  <Text strong style={{ fontSize: 15 }}>{customer.name || "Unknown"}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>{customer.email || customer.mobile || ""}</Text>
                </div>
              </div>
              <div className="delivery-info-grid">
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><BarcodeOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Order ID</div>
                    <div className="delivery-info-value">{orderDetails.orderID || "—"}</div>
                  </div>
                </div>
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><TruckOutlined /></span>
                  <div>
                    <div className="delivery-info-label">AWB Code</div>
                    <div className="delivery-info-value">{shipment.awb_code || orderDetails._scannedAwb}</div>
                  </div>
                </div>
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><TruckOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Courier</div>
                    <div className="delivery-info-value">{shipment.courier_name || "—"}</div>
                  </div>
                </div>
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><CalendarOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Order Date</div>
                    <div className="delivery-info-value">{orderDetails.order_date ? moment(orderDetails.order_date).format("DD MMM YYYY") : "—"}</div>
                  </div>
                </div>
              </div>
              {customer.tags?.length > 0 && (
                <div style={{ marginTop: 10, display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {customer.tags.map((t, i) => <Tag key={i}>{typeof t === "string" ? t : t.name}</Tag>)}
                </div>
              )}
            </div>

            {/* Right: Shipment + Order Info */}
            <div className="delivery-info-right">
              <div className="delivery-info-grid">
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><EnvironmentOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Warehouse</div>
                    <div className="delivery-info-value">{orderDetails.warehouse || "—"}</div>
                  </div>
                </div>
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><LockOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Packed Status</div>
                    <div className="delivery-info-value">{orderDetails.packed_status || "—"}</div>
                  </div>
                </div>
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><BoxPlotOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Products</div>
                    <div className="delivery-info-value">{totalProducts} items · {totalProductsWeight.toFixed(2)} kg</div>
                  </div>
                </div>
                <div className="delivery-info-item">
                  <span className="delivery-info-icon"><ShoppingCartOutlined /></span>
                  <div>
                    <div className="delivery-info-label">Shipment Status</div>
                    <div className="delivery-info-value">{shipment.status || "—"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Status Strip ─── */}
          <div className="delivery-status-strip">
            <Text strong>{statusMessage}</Text>
            {stageTag}
          </div>

          {/* ─── Form Section ─── */}
          <div className="delivery-form-section">

            {/* Default Box Settings */}
            <div className="delivery-default-settings">
              <div className="delivery-default-header" onClick={() => setShowDefaultSettings(!showDefaultSettings)}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SettingOutlined />
                  <Text strong style={{ fontSize: 13 }}>Default Box Settings</Text>
                  {hasDefaultBox && <Tag color="blue" style={{ marginLeft: 4 }}>{defaultBoxType}</Tag>}
                </span>
                <Switch size="small" checked={defaultBoxEnabled} onChange={toggleDefaultBox} disabled={!hasDefaultBox} onClick={(e) => e.stopPropagation()} />
              </div>
              {showDefaultSettings && (
                <div className="delivery-default-body">
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Select
                      value={defaultBoxType || undefined}
                      onChange={setDefaultBoxType}
                      placeholder="Select default box type"
                      style={{ width: 200 }}
                    >
                      {packingMaterialOptions.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                    {!hasDefaultBox ? (
                      <Button size="small" icon={<SaveOutlined />} onClick={saveDefaultBox} disabled={!defaultBoxType}>Save</Button>
                    ) : (
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={clearDefaultBox}>Clear</Button>
                    )}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: "block" }}>
                    When enabled, this box type auto-fills when scanning orders.
                  </Text>
                </div>
              )}
            </div>

            {showWeightInput ? (
              <Form form={form} layout="vertical">
                <div className="delivery-form-row">
                  {/* Weight */}
                  <div>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>Final Package Weight (kg)</Text>
                    <Form.Item
                      name="weight"
                      rules={[
                        { required: true, message: "Enter weight" },
                        { type: "number", min: 0.1, message: "Min 0.1 kg" },
                        { type: "number", max: 100, message: "Max 100 kg" },
                        () => ({
                          validator(_, v) {
                            if (!v || v >= totalProductsWeight) return Promise.resolve();
                            return Promise.reject(new Error(`Must be ≥ ${totalProductsWeight} kg`));
                          },
                        }),
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="delivery-weight-input"
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          form.setFieldsValue({ weight: isNaN(val) ? undefined : val });
                        }}
                      />
                    </Form.Item>
                    <div className="delivery-weight-display" style={{ marginTop: 10 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>Products weight</Text>
                      <div className="delivery-weight-big">{totalProductsWeight.toFixed(2)} kg</div>
                    </div>
                  </div>

                  {/* Box Types */}
                  <div>
                    <div className="delivery-box-heading">
                      <Text strong>Box Types</Text>
                      {orderDetails?.box_type && (
                        <Button
                          type="link"
                          size="small"
                          icon={boxTypeLocked ? <LockOutlined /> : <EditOutlined />}
                          onClick={enableBoxTypeEditing}
                          disabled={!boxTypeLocked}
                        >
                          {boxTypeLocked ? "Change" : "Editing"}
                        </Button>
                      )}
                    </div>
                    {boxTypes.map((bt, i) => (
                      <div key={i} className="delivery-box-row">
                        <Select
                          value={bt.type || undefined}
                          onChange={(v) => handleBoxTypeChange(i, "type", v)}
                          placeholder="Select type"
                          disabled={boxTypeLocked}
                        >
                          {[
                            ...packingMaterialOptions,
                            ...(bt.type && !packingMaterialOptions.some((o) => o.value === bt.type)
                              ? [{ value: bt.type, label: bt.type }]
                              : []),
                          ].map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                        </Select>
                        <InputNumber
                          value={bt.quantity}
                          onChange={(v) => handleBoxTypeChange(i, "quantity", v)}
                          min={1}
                          max={999}
                          placeholder="Qty"
                          controls={false}
                          disabled={boxTypeLocked}
                        />
                        {boxTypes.length > 1 && !boxTypeLocked && (
                          <Button danger icon={<DeleteOutlined />} onClick={() => handleRemoveBoxType(i)} />
                        )}
                      </div>
                    ))}
                    {!boxTypeLocked && (
                      <Button type="dashed" onClick={handleAddBoxType} style={{ width: "100%", marginTop: 8 }}>
                        + Add Box Type
                      </Button>
                    )}
                  </div>
                </div>

                {/* Products View */}
                <Button icon={<ShoppingCartOutlined />} onClick={() => setProductsDrawerOpen(true)} style={{ marginBottom: 12 }}>
                  View Products ({totalProducts})
                </Button>

                {/* Actions */}
                <div className="delivery-actions">
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={submitting}
                    onClick={handleWeightSubmit}
                  >
                    {isEditing ? "Update Delivery" : "Confirm Delivery"}
                  </Button>
                  <Button icon={<ClearOutlined />} danger onClick={clearOrder}>
                    Clear
                  </Button>
                </div>
              </Form>
            ) : deliveryStage === "COMPLETED" ? (
              <>
                <div className="delivery-completed-info">
                  <Text strong style={{ color: "var(--del-success)", display: "block", marginBottom: 10, fontSize: 14 }}>
                    <CheckCircleOutlined /> Delivery Completed
                  </Text>
                  <div className="delivery-completed-grid">
                    <div className="delivery-completed-item">
                      <Text type="secondary" style={{ fontSize: 11 }}>Date</Text>
                      <Text strong>{moment(orderDetails.warehouse_out_date).format("DD MMM YYYY")}</Text>
                    </div>
                    <div className="delivery-completed-item">
                      <Text type="secondary" style={{ fontSize: 11 }}>Time</Text>
                      <Text strong>{orderDetails.warehouse_out_time}</Text>
                    </div>
                    <div className="delivery-completed-item">
                      <Text type="secondary" style={{ fontSize: 11 }}>Weight</Text>
                      <Text strong>{orderDetails.final_package_weight} kg</Text>
                    </div>
                    <div className="delivery-completed-item">
                      <Text type="secondary" style={{ fontSize: 11 }}>Box Type</Text>
                      <Text strong>{orderDetails.box_type || "—"}</Text>
                    </div>
                  </div>
                </div>
                <div className="delivery-actions" style={{ marginTop: 16 }}>
                  <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>Edit Delivery</Button>
                  <Button icon={<ShoppingCartOutlined />} onClick={() => setProductsDrawerOpen(true)}>View Products</Button>
                  <Button icon={<ClearOutlined />} danger onClick={clearOrder}>Clear</Button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <Text type="secondary">{statusMessage}</Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════ PRODUCTS DRAWER ══════ */}
      <Drawer
        title={`Products (${totalProducts}) — ${totalProductsWeight.toFixed(2)} kg`}
        placement="right"
        width={500}
        open={productsDrawerOpen}
        onClose={() => setProductsDrawerOpen(false)}
      >
        <div className="delivery-drawer-products">
          <Table
            dataSource={(orderDetails?.products || []).map((p, i) => ({ key: p.id || i, name: p.name, quantity: p.quantity, weight: getEffectiveWeight(p) }))}
            columns={productColumns}
            pagination={false}
            size="small"
          />
        </div>
      </Drawer>
    </main>
  );
};

export default DeliveryPage;
